// The REPORT sheet (fault-reports.md §4.4, R1626; round 077 cards 08 and 09).
//
// OPTIONS · REPORT opens the sheet; the hub keeps its own way in through
// SUPPORT. The domain term is the **player report** (`CONTEXT.md`); the
// words "REPORT A PROBLEM" are the sheet's title alone, because the REPORT
// of a chat line is the moderation report.
//
// The sheet asks three things:
//
// - **WHAT KIND**: one of the six report topics (R1621). SEND needs one.
// - **WHAT HAPPENED**: free text, at most `text_max_runes`; it may stay
//   empty.
// - **THE REPORT CARRIES**: a checklist the player can thin: the client log
//   (R1627), the world tick, a screenshot taken when the sheet opened with
//   its thumbnail, and the selected fleets. One fixed line states what
//   always goes: the account, the build and the browser.
//
// SEND turns the sheet into one answer (card 09): REPORT SENT with the
// report reference (R1622) and what went out; NOT SENT YET with the wait,
// when this device sent a report less than a minute ago or a bucket of the
// meta server refused (R1628), and the words stay; THE REPORT WAITS, when
// the link is lost and the outbox keeps the report. No answer promises a
// fix.
//
// On a desktop the sheet is a card of the OPTIONS geometry over the dimmed
// map; on a telephone it rises from the foot of the screen, with CANCEL
// and SEND at the thumb. While it stands, the map and the chrome take no
// key. The controls act on the release (`click`), because on a telephone
// the sheet scrolls under the finger (§16.8, R1206).

import { runeCount } from '../util/text';
import { PLAYER_REPORT_TEXT_MAX_RUNES, PLAYER_REPORT_TOPICS } from '../gen/balance';
import { REPORT_SHEET } from '../gen/presentation';
import { reportSheet as S } from '../gen/strings';
import { ApiError, messageOf, type PlayerReportDraft, type PlayerReportReceipt, type PlayerReportTopic } from '../net/api';
import type { LogTail } from '../net/clientlog';
import { deviceGapLeftSec, lostLink, markSent } from '../net/reportoutbox';
import { esc } from './esc';
import { valueChoice } from './choice/choice';
import { hhmm, mmss } from '../locale/clock';
import { groupThousands as group } from '../locale/group';
import { wordOf } from '../locale/wordof';

/** The act of a topic option of the choice kit. */
const TOPIC_ACT = 'rs-topic';

// --- the rules ----------------------------------------------------------------

/** The label of one report topic. */
export function topicLabel(t: PlayerReportTopic): string {
  return wordOf(S, `topic_${t}`, t.toUpperCase());
}

/** The browser of each user agent pattern, in the order of the test: a
 *  browser that names another one in its agent stands before it. */
const BROWSERS: readonly (readonly [RegExp, string])[] = [
  [/Edg(?:e|A|iOS)?\/(\d+)/, 'Edge'],
  [/OPR\/(\d+)/, 'Opera'],
  [/SamsungBrowser\/(\d+)/, 'Samsung Internet'],
  [/Firefox\/(\d+)/, 'Firefox'],
  [/FxiOS\/(\d+)/, 'Firefox'],
  [/CriOS\/(\d+)/, 'Chrome'],
  [/Chrome\/(\d+)/, 'Chrome'],
  [/Version\/(\d+)[\d.]* .*Safari\//, 'Safari'],
];

/** The browser and its main version, from a user agent: "Chrome 141". */
export function browserOf(ua: string): string {
  for (const [re, name] of BROWSERS) {
    const m = re.exec(ua);
    if (m) return `${name} ${m[1]}`;
  }
  return ua.split(' ')[0] ?? '';
}

/** A wait as m:ss, "0:42". It rounds up, so a wait never reads 0:00 early. */
export function waitText(sec: number): string {
  return mmss(Math.ceil(sec));
}

/** A size in whole KB, at least 1. */
function sizeText(bytes: number): string {
  return S.size_kb({ kb: group(Math.max(1, Math.round(bytes / 1024))) });
}

/** The address of the network check page (fault-reports.md §9, R1707). */
export const NETTEST_HREF = '/nettest';

/**
 * The line under the topics while CONNECTION is picked: the link to the
 * network check page (R1707). The link opens in the same window, so a
 * Home Screen app runs the check inside the app, on the path that failed.
 */
function nettestLine(): string {
  return `<div class="rl rnet">${esc(S.nettest_line)} <a href="${NETTEST_HREF}">${esc(S.nettest_link)}</a></div>`;
}

/** What the player keeps on the checklist. */
export interface Carry { log: boolean; tick: boolean; shot: boolean; fleets: boolean }

/** SEND needs a topic, and nothing else (R1621). */
export function canSend(topic: PlayerReportTopic | ''): boolean {
  return topic !== '';
}

/** The list of what went out, for the sent answer: the items the player
 *  kept and the sheet could fill. */
export function sentList(carry: Carry, has: { log: boolean; shot: boolean; fleets: number }): string {
  const out: string[] = [];
  if (carry.log && has.log) out.push(S.log);
  if (carry.tick) out.push(S.tick);
  if (carry.shot && has.shot) out.push(S.shot);
  if (carry.fleets && has.fleets > 0) out.push(S.sent_fleets({ count: has.fleets }));
  return out.join(', ');
}

/**
 * The chat of other players the client log must not hold (R1627): every
 * world chat line that another account wrote, and every clan chat message
 * of another member. The world's own lines (`fromId` 0) are not a
 * player's, and they go too, because the rule is cheaper whole.
 */
export function otherPlayersChat(
  chat: readonly { fromId: number; text: string }[], selfId: number,
  clan: readonly { kind: string; senderId?: string; body?: string }[], clanSelf: string,
): string[] {
  const out: string[] = [];
  for (const l of chat) if (l.fromId !== selfId && l.text) out.push(l.text);
  for (const e of clan) if (e.kind === 'message' && e.senderId !== clanSelf && e.body) out.push(e.body);
  return out;
}

// --- the sheet ----------------------------------------------------------------

/** The facts of the moment the sheet opened. */
export interface SheetFacts {
  worldId: number;
  world: string;
  tick: number;
  day: number;
  /** The world's time of day, in game seconds. */
  clockSec: number;
  fleets: { id: number; name: string }[];
  /** The line of THE WORLD TICK in the place of the world, the day and
   *  the clock, for a page with no season: an arcade match states its map
   *  and its tick (R1936). Absent, the season line stands. */
  line?: string;
  renderer: string;
  page: string;
  build: string;
  browser: string;
}

/** The screenshot the sheet took: the PNG and a thumbnail address. */
export interface SheetShot { png: Uint8Array; thumb: string }

/** What the sheet needs of the page. A test passes plain objects. */
export interface SheetDeps {
  /** The chrome root `#ui`; the sheet and its scrim join it. */
  shell: HTMLElement;
  facts: () => SheetFacts;
  /** The account that sends, or null where none answers. */
  account: () => Promise<{ name: string; guest: boolean } | null>;
  /** The tail of the client log, through the chat filter (R1627). */
  log: () => LogTail;
  /** The screenshot of the map, taken now, or null. */
  shot: () => Promise<SheetShot | null>;
  send: (d: PlayerReportDraft) => Promise<PlayerReportReceipt>;
  /** The outbox of a lost link (R1628). */
  outbox: { push(d: PlayerReportDraft): void };
  store: Storage | null;
  now: () => number;
  /** Copies a text; answers whether it worked. */
  copy: (text: string) => Promise<boolean>;
  /** Other hosts that wear the class `reporting`: the minimap canvas,
   *  which is not inside `#ui`. */
  also?: () => (HTMLElement | null)[];
}

/** Which face the sheet shows. */
export type SheetView = 'closed' | 'form' | 'sending' | 'sent' | 'limit' | 'wait';

export class ReportSheet {
  readonly el: HTMLElement;
  private readonly scrim: HTMLElement;
  private viewNow: SheetView = 'closed';
  topic: PlayerReportTopic | '' = '';
  text = '';
  carry: Carry = { log: true, tick: true, shot: true, fleets: true };
  private facts: SheetFacts | null = null;
  private tail: LogTail = { text: '', lines: 0, bytes: 0 };
  private shotNow: SheetShot | null = null;
  private shotPending = false;
  private accountLine = '';
  private refusal = '';
  private ref = '';
  private sentWith = '';
  private copied = false;
  private waitLeft = 0;
  private waitServer = false;
  private waitTimer = 0;
  /** One open is one session of the sheet: a late answer of an earlier
   *  open must not write into a later one. */
  private session = 0;
  /** True once a press began on the sheet after it opened. The OPTIONS
   *  button that opens the sheet acts on the release, and the browser's
   *  click of that same tap then lands on whatever the sheet put under
   *  the finger; a click with no press of its own is dropped. */
  private pressed = false;

  constructor(private readonly d: SheetDeps) {
    this.scrim = document.createElement('div');
    this.scrim.id = 'reportscrim';
    this.el = document.createElement('div');
    this.el.id = 'reportsheet';
    this.el.className = 'panel';
    this.el.setAttribute('role', 'dialog');
    this.el.setAttribute('aria-label', S.title);
    this.scrim.hidden = true;
    this.el.hidden = true;
    // The scrim stands under every panel of the chrome: it is the first
    // child, so it dims the map and not the chrome.
    d.shell.prepend(this.scrim);
    d.shell.append(this.el);
    this.el.addEventListener('pointerdown', () => { this.pressed = true; }, { capture: true });
    this.el.addEventListener('click', (e) => {
      // A keyboard click (Enter, Space) has `detail` 0 and always counts.
      if (this.pressed || e.detail === 0) return;
      e.preventDefault();
      e.stopImmediatePropagation();
    }, { capture: true });
    this.el.addEventListener('click', (e) => this.onClick(e));
    this.el.addEventListener('input', (e) => this.onInput(e));
    this.el.addEventListener('change', (e) => this.onChange(e));
    // While the sheet stands, the map and the chrome take no key; esc
    // closes the sheet.
    window.addEventListener('keydown', (e) => {
      if (this.viewNow === 'closed') return;
      if (e.key === 'Escape' && this.viewNow !== 'sending') this.close();
      e.stopImmediatePropagation();
    }, { capture: true });
  }

  /** The face the sheet shows. */
  get view(): SheetView { return this.viewNow; }
  /** True while the sheet stands. */
  get isOpen(): boolean { return this.viewNow !== 'closed'; }
  /** The screenshot of this open, or null. */
  get shot(): SheetShot | null { return this.shotNow; }

  /**
   * Opens the sheet: an empty form, the facts of this moment, and a
   * screenshot of the map taken now, before the sheet covers it.
   */
  open(): void {
    const session = ++this.session;
    this.stopWait();
    this.pressed = false;
    this.topic = '';
    this.text = '';
    this.carry = { log: true, tick: true, shot: true, fleets: true };
    this.refusal = '';
    this.ref = '';
    this.copied = false;
    this.shotNow = null;
    this.shotPending = true;
    this.facts = this.d.facts();
    this.tail = this.d.log();
    this.accountLine = '';
    // A shot that fails lands as no shot.
    const land = (s: SheetShot | null): void => {
      if (session !== this.session) return;
      this.shotNow = s;
      this.shotPending = false;
      if (this.viewNow === 'form') this.renderForm();
    };
    void this.d.shot().then(land, () => land(null));
    void this.d.account().then((a) => {
      if (session !== this.session || !a || !this.facts) return;
      const f = this.facts;
      const p = { account: a.name, build: f.build, browser: f.browser };
      this.accountLine = a.guest ? S.always_sub_guest(p) : S.always_sub(p);
      const line = this.el.querySelector('[data-always]');
      if (line) line.textContent = this.accountLine;
    }).catch(() => undefined);
    this.show('form');
  }

  close(): void {
    this.session++;
    this.stopWait();
    this.show('closed');
  }

  /** Picks the topic. */
  pick(t: PlayerReportTopic): void {
    this.topic = t;
    this.refusal = '';
    if (this.viewNow === 'form') this.renderForm();
  }

  /**
   * SEND. It answers the face the sheet shows afterwards.
   */
  async send(): Promise<SheetView> {
    if (this.viewNow !== 'form' || !canSend(this.topic) || !this.facts) return this.viewNow;
    const gap = deviceGapLeftSec(this.d.store, this.d.now());
    if (gap > 0) {
      this.startWait(gap, false);
      return this.viewNow;
    }
    const draft = this.draft();
    const session = this.session;
    this.show('sending');
    try {
      const receipt = await this.d.send(draft);
      if (session !== this.session) return this.viewNow;
      markSent(this.d.store, this.d.now());
      this.ref = receipt.ref;
      this.sentWith = sentList(this.carry, {
        log: this.tail.lines > 0,
        shot: draft.screenshot !== undefined && !receipt.shotDropped,
        fleets: draft.fleets?.length ?? 0,
      });
      this.show('sent');
    } catch (err) {
      if (session !== this.session) return this.viewNow;
      if (lostLink(err)) {
        this.d.outbox.push(draft);
        markSent(this.d.store, this.d.now());
        this.show('wait');
      } else if (err instanceof ApiError && err.status === 429) {
        this.startWait(err.retryAfterSec > 0 ? err.retryAfterSec : REPORT_SHEET.deviceGapSec, true);
      } else {
        this.refusal = S.refused({ reason: messageOf(err) });
        this.show('form');
      }
    }
    return this.viewNow;
  }

  /** The report the form describes, with the parts the player kept. */
  draft(): PlayerReportDraft {
    const f = this.facts;
    const fleets = f && this.carry.fleets ? f.fleets.map((x) => x.id) : [];
    return {
      topic: this.topic as PlayerReportTopic,
      text: this.text,
      logTail: this.carry.log ? this.tail.text : '',
      world: f?.worldId ?? 0,
      tick: f && this.carry.tick ? f.tick : 0,
      renderer: f?.renderer ?? '',
      fleets,
      page: f?.page ?? '',
      build: f?.build ?? '',
      screenshot: this.carry.shot && this.shotNow ? this.shotNow.png : undefined,
    };
  }

  // --- the faces ----------------------------------------------------------------

  private show(v: SheetView): void {
    this.viewNow = v;
    const open = v !== 'closed';
    this.el.hidden = !open;
    this.scrim.hidden = !open;
    // On a telephone the sheet stands alone under the top bar (card 08).
    for (const h of [this.d.shell, ...(this.d.also?.() ?? [])]) h?.classList.toggle('reporting', open);
    this.el.dataset.view = v;
    if (!open) { this.el.innerHTML = ''; return; }
    if (v === 'form' || v === 'sending') this.renderForm();
    else this.renderAnswer();
  }

  /** The form of card 08. It is drawn whole on a change of state and never
   *  on a key press, so the text field keeps its caret. */
  private renderForm(): void {
    const f = this.facts;
    const field = this.el.querySelector<HTMLTextAreaElement>('textarea');
    const hadFocus = field !== null && document.activeElement === field;
    // The topic is the chooser of the choice kit: six topics draw an
    // option list (R2011).
    const topics = valueChoice({
      list: PLAYER_REPORT_TOPICS.map((t) => ({ id: t, label: topicLabel(t) })), chosen: this.topic,
      act: TOPIC_ACT, label: S.kind, uid: TOPIC_ACT,
    });
    const chk = (key: keyof Carry, title: string, sub: string, on: boolean, can: boolean, extra = '') =>
      `<label class="chk${can ? '' : ' off'}"><input type="checkbox" data-c="${key}"${on && can ? ' checked' : ''}${can ? '' : ' disabled'}>`
      + `<span class="ct"><b>${esc(title)}</b><small>${esc(sub)}</small></span>${extra}</label>`;
    const tick = f ? f.line ?? S.tick_sub({
      world: f.world.toUpperCase(), tick: group(f.tick), day: f.day, clock: hhmm(f.clockSec),
    }) : '';
    const fleets = f && f.fleets.length > 0 ? f.fleets.map((x) => x.name).join(', ') : S.fleets_none;
    const shotSub = this.shotNow || this.shotPending ? S.shot_sub : S.shot_none;
    const thumb = this.shotNow
      ? `<span class="thumb"><img alt="" src="${esc(this.shotNow.thumb)}" width="${REPORT_SHEET.thumbWPx}" height="${REPORT_SHEET.thumbHPx}"></span>`
      : '';
    const always = this.accountLine || (f ? S.always_sub({ account: '…', build: f.build, browser: f.browser }) : '');
    const sending = this.viewNow === 'sending';
    this.el.innerHTML = `<div class="rh"><span class="rt">${esc(S.title)}</span>`
      + `<button type="button" class="rx" data-rs="close" title="${esc(S.close_title)}">✕</button></div>`
      + `<div class="rl">${esc(S.lede)}</div>`
      + `<div class="rk">${esc(S.kind)}</div><div class="rtopics">${topics}</div>`
      + (this.topic === 'connection' ? nettestLine() : '')
      + `<div class="rk">${esc(S.happened)}</div>`
      + `<div class="rtext"><textarea maxlength="${PLAYER_REPORT_TEXT_MAX_RUNES}" placeholder="${esc(S.happened_hint)}"`
      + ` aria-label="${esc(S.happened)}"></textarea><span class="cnt"></span></div>`
      + `<div class="rk">${esc(S.carries)}</div>`
      + chk('log', S.log, S.log_sub({ lines: this.tail.lines, size: sizeText(this.tail.bytes) }), this.carry.log, this.tail.lines > 0)
      + chk('tick', S.tick, tick, this.carry.tick, true)
      + chk('shot', S.shot, shotSub, this.carry.shot, this.shotNow !== null || this.shotPending, thumb)
      + chk('fleets', S.fleets, fleets, this.carry.fleets, (f?.fleets.length ?? 0) > 0)
      + `<div class="chk fix"><span class="cb"></span><span class="ct"><b>${esc(S.always)}</b>`
      + `<small data-always>${esc(always)}</small></span></div>`
      + (this.refusal ? `<div class="rerr">${esc(this.refusal)}</div>` : '')
      + `<div class="rf"><span class="fine">${esc(S.fine)}</span>`
      + `<button type="button" data-rs="cancel"${sending ? ' disabled' : ''}>${esc(S.cancel)}</button>`
      + `<button type="button" class="primary" data-rs="send"${canSend(this.topic) && !sending ? '' : ' disabled'}`
      + `${canSend(this.topic) ? '' : ` title="${esc(S.pick_kind)}"`}>${esc(sending ? S.sending : S.send)}</button></div>`;
    const area = this.el.querySelector('textarea') as HTMLTextAreaElement;
    area.value = this.text;
    area.disabled = sending;
    this.count();
    if (hadFocus) area.focus();
  }

  /** The three answers of card 09. */
  private renderAnswer(): void {
    let html = '';
    if (this.viewNow === 'sent') {
      html = `<div class="okh">${esc(S.sent_head)}</div><div class="ref">${esc(this.ref)}</div>`
        + `<div class="rl">${esc(S.sent_body)}${this.sentWith ? ` ${esc(S.sent_with({ list: this.sentWith }))}` : ''}</div>`
        + `<div class="rf"><button type="button" data-rs="copy">${esc(this.copied ? S.copied : S.copy_ref)}</button>`
        + `<button type="button" class="primary" data-rs="close">${esc(S.close)}</button></div>`;
    } else if (this.viewNow === 'limit') {
      const wait = waitText(this.waitLeft);
      const line = (this.waitServer ? S.limit_server : S.limit_device)({ wait: '\u0000' });
      html = `<div class="badh">${esc(S.limit_head)}</div>`
        + `<div class="rl">${esc(line).replace('\u0000', `<b data-wait>${esc(wait)}</b>`)}</div>`
        + `<div class="rf"><button type="button" data-rs="cancel">${esc(S.cancel)}</button>`
        + `<button type="button" disabled data-wait-btn>${esc(S.send_wait({ wait }))}</button></div>`;
    } else if (this.viewNow === 'wait') {
      html = `<div class="badh">${esc(S.wait_head)}</div><div class="rl">${esc(S.wait_body)}</div>`
        + `<div class="rf"><button type="button" class="primary" data-rs="close">${esc(S.close)}</button></div>`;
    }
    this.el.innerHTML = html;
  }

  /** The counter under the text: runes over the cap. */
  private count(): void {
    const cnt = this.el.querySelector('.cnt');
    if (cnt) cnt.textContent = S.count({ count: runeCount(this.text), max: group(PLAYER_REPORT_TEXT_MAX_RUNES) });
  }

  // --- the wait -----------------------------------------------------------------

  private startWait(sec: number, server: boolean): void {
    this.stopWait();
    this.waitLeft = Math.ceil(sec);
    this.waitServer = server;
    this.show('limit');
    this.waitTimer = window.setInterval(() => {
      this.waitLeft--;
      if (this.waitLeft <= 0) {
        this.stopWait();
        // The words stay: the form comes back as the player left it.
        this.show('form');
        return;
      }
      const w = waitText(this.waitLeft);
      const b = this.el.querySelector('[data-wait]');
      if (b) b.textContent = w;
      const btn = this.el.querySelector('[data-wait-btn]');
      if (btn) btn.textContent = S.send_wait({ wait: w });
    }, 1000);
  }

  private stopWait(): void {
    window.clearInterval(this.waitTimer);
    this.waitTimer = 0;
  }

  // --- the controls -------------------------------------------------------------

  private onClick(e: Event): void {
    const btn = (e.target as HTMLElement).closest<HTMLButtonElement>(`button[data-rs], button[data-act="${TOPIC_ACT}"]`);
    if (!btn || btn.disabled) return;
    switch (btn.dataset.rs ?? 'topic') {
      case 'topic': this.pick(btn.dataset.id as PlayerReportTopic); break;
      case 'send': void this.send(); break;
      case 'cancel':
      case 'close': this.close(); break;
      case 'copy':
        void this.d.copy(this.ref).then((ok) => {
          this.copied = ok;
          if (this.viewNow === 'sent') this.renderAnswer();
        });
        break;
    }
  }

  private onInput(e: Event): void {
    const t = e.target as HTMLElement;
    if (t instanceof HTMLTextAreaElement) {
      this.text = t.value;
      this.count();
    }
  }

  private onChange(e: Event): void {
    const t = e.target as HTMLElement;
    if (t instanceof HTMLInputElement && t.type === 'checkbox') {
      const key = t.dataset.c as keyof Carry | undefined;
      if (key) this.carry[key] = t.checked;
    }
  }
}
